import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AuthenticationService, User } from 'src/app/auth/authentication.service';
import { catchError, concatMap, map } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WindowService } from '@shared/services/window-location/window.service';
import { VendorIntegrationService } from '@shared/services/vendor-integration.service';
import { DeepLinkingService } from '@shared/services/deep-linking.service';
import moment from 'moment';

/**
 * Component that receives a temp token from the IDP as a query param,
 * and fetches the JWT access token for the user
 */
@Component({
  selector: 'app-token',
  template: '',
  standalone: false,
})
export class TokenComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthenticationService,
    private externalLocationService: WindowService,
    private vendorIntegrationService: VendorIntegrationService,
    private deeplinkingService: DeepLinkingService,
  ) {}

  ngOnInit() {
    this.auth.tokenBeingExchanged = true;
    // If the route has the temp token has a query param, ask for the JWT Access token,
    // and do all the initial processing needed before going to the home page
    this.subscription.add(
      this.route.paramMap
        .pipe(
          concatMap((params) => {
            return this.getExchangeToken(params);
          }),
          catchError(() => {
            return of(false);
          }),
        )
        .subscribe((success) => {
          if (success) {
            this.onSuccess();
          } else {
            this.onError();
          }
        }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onSuccess() {
    this.notifyPostSignup();

    this.auth.tokenBeingExchanged = false;

    this.vendorIntegrationService.initHeapImageProxy();
    this.vendorIntegrationService
      .heapTrack('Login_Event_Success', {})
      .pipe(catchError(() => of(true)))
      .subscribe(() => {
        this.vendorIntegrationService.rollbackHeapImageProxy();
        this.router.navigate(['/']).then();
      });
  }

  /**
   * If any step of the authentication goes wrong, user should be routed to the login url
   */
  onError() {
    this.externalLocationService.assignLocation(environment.loginUrl);
    return of(null);
  }

  private notifyPostSignup(): void {
    const referralUrl: string | null = this.deeplinkingService.getRedirectionUrl();

    if(!referralUrl) {
      return;
    }

    let referralUrlWithDomain: string;
    try {
      referralUrlWithDomain = new URL(referralUrl, window.location.origin).href;
    } catch (error) {
      referralUrlWithDomain = referralUrl;
    }

    this.subscription.add(
      this.isNewSignup$
        .pipe(
          concatMap(([within5Minutes, user]) => {
            if (within5Minutes) {
              return this.vendorIntegrationService.submitPardotForm(environment.pardotForms.signupForm, {
                username: user.username,
                referralUrl: referralUrlWithDomain,
              });
            }
            return of(true);
          }),
        )
        .subscribe(),
    );
  }

  private get isNewSignup$(): Observable<[boolean, User]> {
    return this.auth
      .me()
      .pipe(map((user) => {
        let within5Minutes = false;

        if(user.createDateTime) {
          within5Minutes = moment().diff(moment(user.createDateTime), 'minutes') < environment.newSignupMinThreshold;
        }

        return [within5Minutes, user];
      }));
  }

  /**
   * Gets the user JWT Token
   * @param params    route query params
   *
   */
  private getExchangeToken(params: ParamMap): Observable<boolean> {
    const token = params.get('token');
    return this.auth.exchangeTempToken(token);
  }
}
